import React, { useState, useEffect } from 'react';
import ClientProfileHeader from './ClientProfileHeader';

import WidgetCustomizationModal from './WidgetCustomizationModal';
import useClientSummary from './hooks/useClientSummary';

const ClientProfile = ({ widgetContents = {}, clientData = null, allClients = [], onClientChange, useCaseId, agentId, domainId, allWidgetsLoaded = false }) => {
  const isIBRole = (sessionStorage.getItem('role') || '').toLowerCase() === 'investment banker';

  const [showCustomizationModal, setShowCustomizationModal] = useState(false);
  const [widgetSettings, setWidgetSettings] = useState(() => {
    const defaultSettings = isIBRole ? {
      summaryPrompt: "Generate a concise brief on this financial institution as a financial institution client (not a retail investor). Include one short sentence overview and a 2-3 sentence analysis covering: current wallet share, KYC status, active deal pipeline, recent engagement level, and key opportunities or risks for the upcoming coverage call. Do not mention risk tolerance or investment horizon.",
      showShortSummary: true,
      showLongSummary: true,
      showClientSelector: true,
      showScheduleButton: true,
      metrics: {
        proxyYtd: true,
        pipelineWeight: true,
        engagementScore: true,
        openAlerts: true,
      },
      tooltips: {
        proxyYtd: "Year-to-date performance of the benchmark proxy most relevant to this institution's holdings.",
        pipelineWeight: "Total value of live and prospective deals in the pipeline with this institution.",
        engagementScore: "Engagement score across recent touchpoints and interactions with this institution.",
        openAlerts: "Number of open action items, compliance flags, or follow-up alerts for this institution.",
      },
      headerColor: '#ffffff',
      textColor: '#000000'
    } : {
      summaryPrompt: "Generate a concise summary of the client's portfolio and key insights. Include one short sentence summary and a more detailed 2-3 sentence analysis.",
      showShortSummary: true,
      showLongSummary: true,
      showClientSelector: true,
      showScheduleButton: true,
      metrics: {
        aum: true,
        ytdReturn: true,
        timeHorizon: true,
        lastContact: true,
        riskTolerance: false,
        investmentGoal: false,
        nextReview: false,
        clientSince: false
      },
      tooltips: {
        aum: "Assets Under Management (AUM) represents the total market value of investments that the client has entrusted to your firm to manage.",
        ytdReturn: "Year-to-Date (YTD) Return shows the percentage gain or loss on the client's investments since the beginning of the current calendar year.",
        timeHorizon: "Time Horizon indicates the client's investment timeframe, which influences their investment strategy and risk tolerance.",
        lastContact: "Last Contact Date shows when you last communicated with this client, helping you maintain regular engagement.",
        riskTolerance: "The client's willingness to accept market volatility in exchange for potentially higher returns.",
        investmentGoal: "The primary financial objective the client is working towards.",
        nextReview: "The scheduled date for the next portfolio review with this client.",
        clientSince: "The date when this individual became your client."
      },
      headerColor: "#ffffff",
      textColor: "#000000"
    };


    const savedSettings = localStorage.getItem(isIBRole ? 'ibClientProfileWidgetSettings' : 'clientProfileWidgetSettings');


    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        // Migrate old avgEngagementScore key to engagementScore
        if (parsed.metrics?.avgEngagementScore !== undefined) {
          parsed.metrics.engagementScore = parsed.metrics.avgEngagementScore;
          delete parsed.metrics.avgEngagementScore;
        }
        if (parsed.tooltips?.avgEngagementScore !== undefined) {
          parsed.tooltips.engagementScore = parsed.tooltips.avgEngagementScore;
          delete parsed.tooltips.avgEngagementScore;
        }
        const merged = { ...defaultSettings, ...parsed };
        return merged;
      } catch (e) {
        console.error('Failed to parse saved settings:', e);
        return defaultSettings;
      }
    }
    return defaultSettings;
  });
  const [summaryGeneratedAfterLoad, setSummaryGeneratedAfterLoad] = useState(false);

  const {
    loading,
    error,
    summary,
    noClientsAvailable,
    isLoadingCustomerData,
    customerData,
    fetchSummary
  } = useClientSummary({
    widgetContents,
    clientData,
    allClients,
    widgetSettings,
    useCaseId,
    agentId,
    domainId,
    skipInitialFetch: !allWidgetsLoaded,
    isIBRole
  });

  useEffect(() => {
    localStorage.setItem(isIBRole ? 'ibClientProfileWidgetSettings' : 'clientProfileWidgetSettings', JSON.stringify(widgetSettings));
  }, [widgetSettings]);

  useEffect(() => {
    if (allWidgetsLoaded && !summaryGeneratedAfterLoad && Object.keys(widgetContents).length > 0) {
      fetchSummary(true);
      setSummaryGeneratedAfterLoad(true);
    }
  }, [allWidgetsLoaded, summaryGeneratedAfterLoad, fetchSummary]);

  useEffect(() => {
    if (Object.keys(widgetContents).length === 0) {
      setSummaryGeneratedAfterLoad(false);
    }
  }, [widgetContents]);

  useEffect(() => {
    const contentCount = Object.keys(widgetContents).length;
    if (contentCount === 0 && summaryGeneratedAfterLoad) {
      setSummaryGeneratedAfterLoad(false);
    }
  }, [widgetContents, summaryGeneratedAfterLoad]);


  const handleRefresh = () => {
    fetchSummary(true);
  };

  const handleSaveSettings = (newSettings) => {
    const promptChanged = newSettings.summaryPrompt !== widgetSettings.summaryPrompt;
    setWidgetSettings(newSettings);

    if (promptChanged && Object.keys(widgetContents).length > 0) {
      setTimeout(() => {
        fetchSummary(true);
      }, 300);
    }
  };

  const shortSummary = summary?.shortSummary || "";
  const longSummary = summary?.longSummary || "";

  return (
    <div className="bg-gray-50 p-4">
      <div className="max-w-full mx-auto">
        {isLoadingCustomerData ? (
          <div className="text-center py-4 text-gray-600">Loading customer data...</div>
        ) : noClientsAvailable ? (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mx-auto text-center">
            <div className="py-8">
              <div className="text-xl font-semibold text-gray-700 mb-2">No Clients Available</div>
              <p className="text-gray-500">
                You don't have access to any clients. Please contact your administrator for assistance.
              </p>
            </div>
          </div>
        ) : (
          <>
          <ClientProfileHeader
            customerData={customerData}
            widgetContents={widgetContents}
            longSummary={longSummary}
            shortSummary={shortSummary}
            isLoading={loading}
            onRefresh={handleRefresh}
            allClients={allClients}
            onClientChange={onClientChange}
            widgetSettings={widgetSettings}
            onEditWidget={() => setShowCustomizationModal(true)}
            isIBRole={isIBRole}
            useCaseId={useCaseId}
            agentId={agentId}
          />
          </>
        )}
      </div>

      {/* Widget Customization Modal */}
      <WidgetCustomizationModal
        isOpen={showCustomizationModal}
        onClose={() => setShowCustomizationModal(false)}
        currentSettings={widgetSettings}
        onSave={handleSaveSettings}
        isIBRole={isIBRole}
      />
    </div>
  );
};

export default ClientProfile;